import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import DropdownButton from '../DropdownButton';
import { TruncatedTooltip } from '../TruncatedTooltip/TruncatedTooltip';

import type { ResourceType } from './types';

import styles from './SavedResource.module.less';

//  Represents one of the dropdown items.
export type ResourceOption = {
  id: number | null;
  name: string;
  isProtected?: boolean;
};

type Props = {
  resourceType: ResourceType;
  resources: ResourceOption[];
  currentResource: ResourceOption;
  isLoading: boolean;
  /**
   * Whether to hide the id of the resource in the dropdown. Useful for resources that need to be identified by users
   * in the CLI by ID, such as saved issue filters.
   */
  shouldHideId?: boolean;
  onChange: (resource: ResourceOption['id']) => void;
  /** Whether the user loading the resources is restricted from viewing them. */
  isRestricted?: boolean;
};

type DropdownTitleProps = {
  resourceType: ResourceType;
  currentResourceName: string;
  isLoading: boolean;
  isRestricted?: boolean;
};

const DropdownTitle: VoidFunctionComponent<DropdownTitleProps> = ({
  resourceType,
  currentResourceName,
  isLoading,
  isRestricted,
}) => {
  if (isRestricted) {
    return <>None</>;
  }
  if (isLoading) {
    return <>Loading...</>;
  }
  return (
    <TruncatedTooltip
      id={`saved-${resourceType}-selected-tooltip`}
      text={currentResourceName}
      className={styles.dropdownButtonText}
    />
  );
};

/**
 * SavedResourceDropdownList renders the list of saved resources
 * for a given resource type.
 */
export const SavedResourceDropdownList: VoidFunctionComponent<Props> = ({
  resourceType,
  resources,
  currentResource,
  isLoading,
  shouldHideId,
  onChange,
  isRestricted,
}) => {
  const title = (
    <DropdownTitle
      resourceType={resourceType}
      currentResourceName={currentResource.name}
      isLoading={isLoading}
      isRestricted={isRestricted}
    />
  );

  return (
    <DropdownButton
      className={styles.savedResourceDropdown}
      id={`saved-${resourceType}-list-dropdown`}
      title={title}
      size="large"
      onSelect={(e) => onChange(e as number | null)}
      testId={`saved-${resourceType}-list-dropdown`}
      isFullWidth
    >
      {resources.map(({ id, name }) => (
        <DropdownButton.Item
          key={id}
          eventKey={id}
          className={cx({
            'fossa-dropdown-button__item--selected': currentResource.id === id,
          })}
        >
          <TruncatedTooltip
            id={`saved-${resourceType}-item-${id}-tooltip`}
            text={name}
            className="fossa-dropdown-button__item__name"
          />
          {!shouldHideId && id && <span>(id: {id})</span>}
        </DropdownButton.Item>
      ))}
    </DropdownButton>
  );
};
